import { FC, createContext, useCallback, useContext, useMemo } from 'react';

import { useAuth } from '~/modules/auth/hooks/auth';

import { useHeader } from './header';
import { useDisplay } from './display';
import { OrderStatusEnum } from '../enums/OrderStatusEnum';

export enum ActionButtonTagEnum {
  'start' = 'start',
  'done' = 'done',
  'wait' = 'wait',
  'reopen' = 'reopen',
  'deliver' = 'deliver',
}

export interface IActionButton {
  title: string;
  confirmModalTitle: string;
  screen: OrderStatusEnum[];
  backgroundColor: string;
  tag: ActionButtonTagEnum;
}

interface IOrderActionButtonsContextData {
  actionButtons: IActionButton[];
  getActionButtonsForSelectedStatus: (
    orderStatus?: OrderStatusEnum
  ) => IActionButton[];
}

const OrderActionButtonsContext = createContext(
  {} as IOrderActionButtonsContextData
);

const OrderActionButtonsProvider: FC = ({ children }) => {
  const { configuration } = useAuth();
  const { selectedInHeader } = useHeader();
  const { statusJourney } = useDisplay();

  const actionButtons: IActionButton[] = useMemo(
    () => [
      {
        title: 'Iniciar tudo',
        confirmModalTitle: 'Deseja realmente iniciar todos os itens?',
        screen: [OrderStatusEnum.open, OrderStatusEnum.waiting],
        backgroundColor: '#f0800b',
        tag: ActionButtonTagEnum.start,
      },
      {
        title: 'Pronto',
        confirmModalTitle: 'Deseja realmente finalizar todos os itens?',
        screen: [
          OrderStatusEnum.open,
          OrderStatusEnum.waiting,
          OrderStatusEnum['in-progress'],
        ],
        backgroundColor: '#00E965',
        tag: ActionButtonTagEnum.done,
      },
      {
        title: 'Esperar',
        confirmModalTitle: 'Deseja realmente colocar o pedido em espera?',
        screen: [OrderStatusEnum.open, OrderStatusEnum['in-progress']],
        backgroundColor: '#F1C000',
        tag: ActionButtonTagEnum.wait,
      },
      {
        title: configuration?.settings?.worksWithReversalStatus
          ? 'Retornar'
          : 'Reabrir',
        confirmModalTitle: `Deseja realmente ${
          configuration?.settings?.worksWithReversalStatus
            ? 'retornar'
            : 'reabrir'
        } o pedido?`,
        screen: [
          OrderStatusEnum.waiting,
          OrderStatusEnum.done,
          OrderStatusEnum.delivered,
        ],
        backgroundColor: '#FFF',
        tag: ActionButtonTagEnum.reopen,
      },
      {
        title: 'Entregar',
        confirmModalTitle: 'Deseja realmente entregar o pedido?',
        screen: [OrderStatusEnum.done],
        backgroundColor: '#B1FEBE',
        tag: ActionButtonTagEnum.deliver,
      },
    ],
    [configuration]
  );

  const displayIncludesStatus = useCallback(
    (status: OrderStatusEnum) => {
      if (!configuration) return [];
      if (!statusJourney || !statusJourney.length) return [];

      return statusJourney.includes(status);
    },
    [configuration, statusJourney]
  );

  const getActionButtonsForSelectedStatus = (
    orderStatus?: OrderStatusEnum
  ): IActionButton[] => {
    let filteredActionButtons: IActionButton[] = [];
    const currentOptionOnHeader =
      selectedInHeader as unknown as OrderStatusEnum;
    const targetStatus = orderStatus || currentOptionOnHeader;

    if (
      targetStatus === OrderStatusEnum.open ||
      targetStatus === OrderStatusEnum['in-progress'] ||
      targetStatus === OrderStatusEnum.waiting
    ) {
      const filtered = actionButtons.filter((actButton) =>
        actButton.screen.includes(targetStatus)
      );

      const includesWaitingStatus = displayIncludesStatus(
        OrderStatusEnum.waiting
      );

      if (
        targetStatus === OrderStatusEnum.open &&
        displayIncludesStatus(OrderStatusEnum['in-progress'])
      ) {
        filteredActionButtons = filtered.filter(
          (f) =>
            f.tag !== ActionButtonTagEnum.done &&
            (!includesWaitingStatus ? f.tag !== ActionButtonTagEnum.wait : true)
        );
      } else {
        filteredActionButtons = filtered.filter(
          (f) =>
            f.tag !== ActionButtonTagEnum.start &&
            (!includesWaitingStatus ? f.tag !== ActionButtonTagEnum.wait : true)
        );
      }
    } else {
      const includesDeliveredStatus = displayIncludesStatus(
        OrderStatusEnum.delivered
      );

      filteredActionButtons = actionButtons.filter(
        (actButton) =>
          actButton.screen.includes(targetStatus) &&
          (!includesDeliveredStatus
            ? actButton.tag !== ActionButtonTagEnum.deliver
            : true)
      );
    }

    return filteredActionButtons;
  };

  return (
    <OrderActionButtonsContext.Provider
      value={{
        actionButtons,
        getActionButtonsForSelectedStatus,
      }}
    >
      {children}
    </OrderActionButtonsContext.Provider>
  );
};

const useOrderActionButtons = (): IOrderActionButtonsContextData => {
  return useContext(OrderActionButtonsContext);
};

export { OrderActionButtonsProvider, useOrderActionButtons };
